import { 
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Toolbar,
  Typography,
  Box,
  useMediaQuery,
  useTheme,
  IconButton,
  Collapse,
  CircularProgress,
} from "@mui/material";
import { Link, useLocation } from "react-router-dom";
import { useState } from "react";
import { useAppAuth } from "../context/AuthContext";

// Icons
import DashboardIcon from "@mui/icons-material/Dashboard";
import StoreIcon from "@mui/icons-material/Store";
import HomeWorkIcon from "@mui/icons-material/HomeWork";
import BuildCircleIcon from "@mui/icons-material/BuildCircle";
import HomeIcon from "@mui/icons-material/Home";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import LocalOfferIcon from "@mui/icons-material/LocalOffer"; // ✅ Deals icon

const drawerWidth = 260;

const Sidebar = () => {
  const { appUser, loading: authLoading } = useAppAuth();
  const location = useLocation();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [mobileOpen, setMobileOpen] = useState(false);

  const toggleDrawer = () => setMobileOpen(!mobileOpen);

  // Collapsible state
  const [openBusiness, setOpenBusiness] = useState(true);

  if (authLoading || !appUser) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const userRole = appUser.role; // ✅ directly from backend

  // Menus
  const menus = {
    admin: [
      { text: "Dashboard", icon: <DashboardIcon />, to: "/" },
      {
        text: "Business",
        collapsible: true,
        items: [
          { text: "Shops", icon: <StoreIcon />, to: "/shops" },
          { text: "Services", icon: <BuildCircleIcon />, to: "/services" },
        ],
      },
      { text: "Airbnbs", icon: <HomeWorkIcon />, to: "/airbnbs" },
      { text: "Properties", icon: <HomeIcon />, to: "/properties" },
    ],
    seller: [
      { text: "Dashboard", icon: <DashboardIcon />, to: "/" },
      { text: "My Shop", icon: <StoreIcon />, to: "/shops/my" },
      // ✅ Add Deals only for premium sellers
      ...(appUser?.is_premium
        ? [{ text: "Deals", icon: <LocalOfferIcon />, to: "/deals" }]
        : []),
    ],
    super_admin: [
      { text: "Dashboard", icon: <DashboardIcon />, to: "/" },
      { text: "All Users", icon: <BuildCircleIcon />, to: "/users" },
      {
        text: "Business",
        collapsible: true,
        items: [
          { text: "Shops", icon: <StoreIcon />, to: "/shops" },
          { text: "Services", icon: <BuildCircleIcon />, to: "/services" },
        ],
      },
      { text: "Airbnbs", icon: <HomeWorkIcon />, to: "/airbnbs" },
      { text: "Properties", icon: <HomeIcon />, to: "/properties" },
    ],
  };

  const menuItems = menus[userRole] || menus.seller;

  // Drawer content
  const drawerContent = (
    <>
      <Toolbar sx={{ justifyContent: "center", py: 2, position: "relative" }}>
        <Typography
          variant="h6"
          sx={{ fontWeight: "bold", fontSize: "1.5rem", color: "#fff" }}
        >
          {userRole === "admin"
            ? "Admin Panel"
            : userRole === "super_admin"
            ? "Super Admin Panel"
            : "Seller Panel"}
        </Typography>
        {isMobile && (
          <IconButton
            onClick={toggleDrawer}
            sx={{ position: "absolute", right: 8, color: "#fff" }}
          >
            <CloseIcon />
          </IconButton>
        )}
      </Toolbar>

      <Box sx={{ overflow: "auto" }}>
        <List>
          {menuItems.map((item) => {
            if (item.collapsible) {
              return (
                <Box key={item.text}>
                  <ListItem
                    button
                    onClick={() => setOpenBusiness(!openBusiness)}
                    sx={{
                      "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" },
                      py: 2,
                    }}
                  >
                    <ListItemIcon sx={{ color: "#fff", minWidth: 40 }}>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.text}
                      primaryTypographyProps={{
                        fontSize: "1.25rem",
                        fontWeight: 600,
                        color: "#fff",
                      }}
                    />
                    {openBusiness ? (
                      <ExpandLess sx={{ color: "#fff" }} />
                    ) : (
                      <ExpandMore sx={{ color: "#fff" }} />
                    )}
                  </ListItem>
                  <Collapse in={openBusiness} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                      {item.items.map(({ text, icon, to }) => (
                        <ListItem
                          key={text}
                          button
                          component={Link}
                          to={to}
                          selected={location.pathname === to}
                          onClick={isMobile ? toggleDrawer : undefined}
                          sx={{
                            pl: 6,
                            "&.Mui-selected": {
                              backgroundColor: "rgba(255,255,255,0.2)",
                            },
                            "&.Mui-selected:hover": {
                              backgroundColor: "rgba(255,255,255,0.25)",
                            },
                            "&:hover": {
                              backgroundColor: "rgba(255,255,255,0.1)",
                            },
                            py: 1.5,
                          }}
                        >
                          <ListItemIcon sx={{ color: "#fff", minWidth: 40 }}>
                            {icon}
                          </ListItemIcon>
                          <ListItemText
                            primary={text}
                            primaryTypographyProps={{
                              fontSize: "1.1rem",
                              fontWeight: 500,
                              color: "#fff",
                            }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Collapse>
                </Box>
              );
            }

            return (
              <ListItem
                key={item.text}
                button
                component={Link}
                to={item.to}
                selected={location.pathname === item.to}
                onClick={isMobile ? toggleDrawer : undefined}
                sx={{
                  "&.Mui-selected": {
                    backgroundColor: "rgba(255,255,255,0.2)",
                  },
                  "&.Mui-selected:hover": {
                    backgroundColor: "rgba(255,255,255,0.25)",
                  },
                  "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" },
                  py: 2,
                }}
              >
                <ListItemIcon sx={{ color: "#fff", minWidth: 40 }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.text}
                  primaryTypographyProps={{
                    fontSize: "1.25rem",
                    fontWeight: 600,
                    color: "#fff",
                  }}
                />
              </ListItem>
            );
          })}
        </List>
      </Box>
    </>
  );

  return (
    <>
      {isMobile && (
        <IconButton
          onClick={toggleDrawer}
          sx={{
            position: "fixed",
            top: 16,
            left: 16,
            zIndex: theme.zIndex.drawer + 1,
            color: "#0fc06d",
            backgroundColor: "#fff",
            "&:hover": { backgroundColor: "#f0f0f0" },
          }}
        >
          <MenuIcon />
        </IconButton>
      )}

      <Drawer
        variant={isMobile ? "temporary" : "permanent"}
        anchor="left"
        open={isMobile ? mobileOpen : true}
        onClose={toggleDrawer}
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          [`& .MuiDrawer-paper`]: {
            width: drawerWidth,
            boxSizing: "border-box",
            background: "linear-gradient(180deg, #0fc06dff, #2a5298)",
            color: "#fff",
          },
        }}
      >
        {drawerContent}
      </Drawer>
    </>
  );
};

export default Sidebar;
